import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Alert,
  CircularProgress,
  Divider,
  Stack,
} from '@mui/material';
import {
  Visibility,
  CheckCircle,
  Cancel,
  Delete,
  Search,
  Refresh,
  FilterList,
} from '@mui/icons-material';
import {
  listJurisprudence,
  getJurisprudenceDetail,
  validateJurisprudence,
  deleteJurisprudence,
  type JurisprudenceItem,
  type JurisprudenceDetail,
} from '../../services/jurisprudenceAdminService';

const JurisprudenceListPage: React.FC = () => {
  const [items, setItems] = useState<JurisprudenceItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [validationStatusFilter, setValidationStatusFilter] = useState('');
  const [courtFilter, setCourtFilter] = useState('');
  const [domainFilter, setDomainFilter] = useState('');

  // Detalhes/Validação
  const [selectedItem, setSelectedItem] = useState<JurisprudenceDetail | null>(null);
  const [detailDialog, setDetailDialog] = useState(false);
  const [validationDialog, setValidationDialog] = useState(false);
  const [validationAction, setValidationAction] = useState<'validated' | 'rejected'>('validated');
  const [validationNotes, setValidationNotes] = useState('');
  const [validating, setValidating] = useState(false);

  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    loadData();
  }, [page, rowsPerPage, searchTerm, validationStatusFilter, courtFilter, domainFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await listJurisprudence(page * rowsPerPage, rowsPerPage, {
        search: searchTerm || undefined,
        validation_status: validationStatusFilter || undefined,
        court_name: courtFilter || undefined,
        legal_domain: domainFilter || undefined,
      });
      setItems(data.items);
      setTotal(data.total);
    } catch (error: any) {
      console.error('Erro ao carregar jurisprudências:', error);
      setMessage({ type: 'error', text: 'Erro ao carregar dados' });
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (item: JurisprudenceItem) => {
    try {
      const detail = await getJurisprudenceDetail(item.id);
      setSelectedItem(detail);
      setDetailDialog(true);
    } catch (error: any) {
      setMessage({ type: 'error', text: 'Erro ao carregar detalhes' });
    }
  };

  const handleOpenValidation = (item: JurisprudenceItem, action: 'validated' | 'rejected') => {
    setSelectedItem(item as JurisprudenceDetail);
    setValidationAction(action);
    setValidationNotes('');
    setValidationDialog(true);
  };

  const handleValidate = async () => {
    if (!selectedItem) return;

    try {
      setValidating(true);
      const result = await validateJurisprudence(selectedItem.id, validationAction, validationNotes);
      
      if (result.success) {
        setMessage({
          type: 'success',
          text: `Jurisprudência ${validationAction === 'validated' ? 'validada' : 'rejeitada'} com sucesso!`,
        });
        setValidationDialog(false);
        loadData(); // Recarregar lista
      } else {
        setMessage({ type: 'error', text: result.message });
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Erro ao validar' });
    } finally {
      setValidating(false);
    }
  };

  const handleDelete = async (item: JurisprudenceItem) => {
    if (!confirm(`Deseja realmente desativar a jurisprudência "${item.title.substring(0, 50)}..."?`)) {
      return;
    }

    try {
      const result = await deleteJurisprudence(item.id);
      if (result.success) {
        setMessage({ type: 'success', text: 'Jurisprudência desativada com sucesso!' });
        loadData();
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: 'Erro ao desativar jurisprudência' });
    }
  };

  const getValidationStatusColor = (status: string) => {
    switch (status) {
      case 'validated':
        return 'success';
      case 'rejected':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
          Jurisprudências Validadas
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Gerencie e valide jurisprudências importadas do Pandora
        </Typography>
      </Box>

      {message && (
        <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      {/* Filtros */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
          <FilterList sx={{ mr: 1, verticalAlign: 'middle' }} />
          Filtros
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Buscar"
              placeholder="Título ou ementa"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status de Validação</InputLabel>
              <Select
                value={validationStatusFilter}
                label="Status de Validação"
                onChange={(e) => setValidationStatusFilter(e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="validated">Validadas</MenuItem>
                <MenuItem value="pending">Pendentes</MenuItem>
                <MenuItem value="rejected">Rejeitadas</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Tribunal</InputLabel>
              <Select
                value={courtFilter}
                label="Tribunal"
                onChange={(e) => setCourtFilter(e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="STF">STF</MenuItem>
                <MenuItem value="STJ">STJ</MenuItem>
                <MenuItem value="TST">TST</MenuItem>
                <MenuItem value="TJSP">TJSP</MenuItem>
                <MenuItem value="TJRJ">TJRJ</MenuItem>
                <MenuItem value="TJMG">TJMG</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<Refresh />}
              onClick={loadData}
              disabled={loading}
              sx={{ height: '56px' }}
            >
              Atualizar
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabela */}
      <Paper>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : items.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">Nenhuma jurisprudência encontrada</Typography>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Título</TableCell>
                    <TableCell>Tribunal</TableCell>
                    <TableCell>Data Julgamento</TableCell>
                    <TableCell>Área</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'medium', maxWidth: 400 }}>
                          {item.title.length > 80 ? item.title.substring(0, 80) + '...' : item.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {item.ementa.length > 100 ? item.ementa.substring(0, 100) + '...' : item.ementa}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={item.court_name} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>{formatDate(item.judgment_date)}</TableCell>
                      <TableCell>
                        {item.legal_domain ? (
                          <Chip label={item.legal_domain} size="small" color="info" />
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={item.validation_status}
                          size="small"
                          color={getValidationStatusColor(item.validation_status) as any}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={1} justifyContent="center">
                          <Tooltip title="Ver Detalhes">
                            <IconButton size="small" color="primary" onClick={() => handleViewDetails(item)}>
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                          {item.validation_status !== 'validated' && (
                            <Tooltip title="Validar">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleOpenValidation(item, 'validated')}
                              >
                                <CheckCircle />
                              </IconButton>
                            </Tooltip>
                          )}
                          {item.validation_status !== 'rejected' && (
                            <Tooltip title="Rejeitar">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleOpenValidation(item, 'rejected')}
                              >
                                <Cancel />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Desativar">
                            <IconButton size="small" color="error" onClick={() => handleDelete(item)}>
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              labelRowsPerPage="Itens por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            />
          </>
        )}
      </Paper>

      {/* Dialog de Detalhes */}
      <Dialog
        open={detailDialog}
        onClose={() => setDetailDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Detalhes da Jurisprudência</DialogTitle>
        <DialogContent dividers>
          {selectedItem && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {selectedItem.title}
              </Typography>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Tribunal
                  </Typography>
                  <Typography variant="body2">{selectedItem.court_name}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Órgão Julgador
                  </Typography>
                  <Typography variant="body2">{selectedItem.judging_body || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Data Julgamento
                  </Typography>
                  <Typography variant="body2">{formatDate(selectedItem.judgment_date)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Número CNJ
                  </Typography>
                  <Typography variant="body2">{selectedItem.cnj_number || '-'}</Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Ementa
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
                {selectedItem.ementa}
              </Typography>

              {selectedItem.binding_thesis && (
                <>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Tese Vinculante
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
                    {selectedItem.binding_thesis}
                  </Typography>
                </>
              )}

              {selectedItem.summary && (
                <>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Resumo
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
                    {selectedItem.summary}
                  </Typography>
                </>
              )}

              <Divider sx={{ my: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Status de Validação
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip
                      label={selectedItem.validation_status}
                      size="small"
                      color={getValidationStatusColor(selectedItem.validation_status) as any}
                    />
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Validado por
                  </Typography>
                  <Typography variant="body2">{selectedItem.validated_by || '-'}</Typography>
                </Grid>
                {selectedItem.keywords && selectedItem.keywords.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      Palavras-chave
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      {selectedItem.keywords.map((keyword, idx) => (
                        <Chip key={idx} label={keyword} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                      ))}
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialog(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Validação */}
      <Dialog
        open={validationDialog}
        onClose={() => !validating && setValidationDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {validationAction === 'validated' ? 'Validar Jurisprudência' : 'Rejeitar Jurisprudência'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Observações"
            placeholder={
              validationAction === 'validated'
                ? 'Motivo da validação (opcional)'
                : 'Motivo da rejeição (opcional)'
            }
            value={validationNotes}
            onChange={(e) => setValidationNotes(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setValidationDialog(false)} disabled={validating}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleValidate}
            disabled={validating}
            color={validationAction === 'validated' ? 'success' : 'error'}
            startIcon={validating ? <CircularProgress size={16} /> : null}
          >
            {validating ? 'Processando...' : validationAction === 'validated' ? 'Validar' : 'Rejeitar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default JurisprudenceListPage;





